import { DefaultError } from "@tanstack/react-query";
import { Button } from "components/atoms";
import { Form } from "components/organisms";
import { userFormInitialValues } from "components/organisms/forms/user/UserForm";
import { FormikHelpers } from "formik";
import { useParamId } from "hooks/useParamId";
import middleware from "middleware";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PiVinylRecord, PiX } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import {
  useDisableTwoFactor,
  useEnableTwoFactor,
} from "services/auth/disableTwoFactor";
import { useGetUser } from "services/userManagement";
import {
  useActivateUser,
  useDeactivateUser,
  useEditUser,
} from "services/userManagement/index";
import {
  useGetPermissions,
  useCheckPermissions,
  useUpdatePermissions,
} from "services/userManagement/managePermissions";
import { IEditUserBody, IRegisterUserForm } from "services/userManagement/types";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { onErrorShowToast } from "utils/service/errorHandler";
import isEqual from "lodash/isEqual";
import PopupCheckPermissions from "components/molecules/popup/check-permissions/PopupCheckPermissions";
import { PopupActions } from "reactjs-popup/dist/types";
import toast from "react-hot-toast";

const EditUser = () => {
  const userId = useParamId("users");
  const { data: user, isLoading: isUserLoading } = useGetUser(userId);
  const { data: permissionsData, isLoading: isPermissionsLoading } =
    useGetPermissions(userId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const backPathname = `/settings/users/${userId}/permissions`;
  const i18nextPrefix = "user";
  const isLoading = isUserLoading || isPermissionsLoading;
  const [showCheckPermissionsPopup, setShowCheckPermissionsPopup] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState<IRegisterUserForm | null>(null);
  const [requiredPermissionsData, setRequiredPermissionsData] = useState<Array<{
    permissionName: string;
    flag: number;
    requiredPermissionName: string;
    requiredFlag: number;
  }> | undefined>(undefined);

  const activateUser = useActivateUser(userId);
  const deactivateUser = useDeactivateUser(userId);
  const editUser = useEditUser(userId);

  const enabelTwoFactor = useEnableTwoFactor(userId);
  const disableTwoFactor = useDisableTwoFactor(userId);

  const updatePermissions = useCheckPermissions(userId);
  const { mutateAsync: updatePermissionsMutation } = useUpdatePermissions(userId);

  const combinedInitialValues = useMemo(() => {
    if (!user || !permissionsData) return userFormInitialValues;

    return {
      ...middleware.user.queryToForm(user, permissionsData),
    };
  }, [user, permissionsData]);

  const handleCheckPermissionsCancel = (actions: PopupActions) => {
    setShowCheckPermissionsPopup(false);
    setRequiredPermissionsData(undefined);
    actions.close();
  };

  const handleCheckPermissionsUpdate = async (actions: PopupActions, formValues: IRegisterUserForm) => {
    try {
      if (requiredPermissionsData && requiredPermissionsData.length > 0) {
        const uniqueRequiredPermissions = requiredPermissionsData.filter((permission, index, self) => 
          index === self.findIndex(p => 
            p.requiredPermissionName === permission.requiredPermissionName && 
            p.requiredFlag === permission.requiredFlag
          )
        );

        const requiredPermissionsToSend = uniqueRequiredPermissions.map(permission => ({
          item1: {
            userId: userId,
            permissionDomainName: permission.requiredPermissionName,
            permissionFlagValue: permission.requiredFlag
          },
          item2: true
        }));

        const originalPermissions = middleware.user.formToQuery(formValues, userId);
        const originalPermissionsToSend = originalPermissions.permissions || [];

        const allPermissionsToSend = [...requiredPermissionsToSend, ...originalPermissionsToSend];

        const uniqueAllPermissions = allPermissionsToSend.filter((permission, index, self) => 
          index === self.findIndex(p => 
            p.item1.permissionDomainName === permission.item1.permissionDomainName && 
            p.item1.permissionFlagValue === permission.item1.permissionFlagValue
          )
        );

        await updatePermissionsMutation({
          permissions: uniqueAllPermissions
        });
      }
      
      setShowCheckPermissionsPopup(false);
      setRequiredPermissionsData(undefined);
      actions.close();
      
      toast.success(t("permissions.update.success"));
      
      navigate(backPathname);
    } catch (error: any) {
      onErrorShowToast(error);
    }
  };

  const handleSubmit = async (
    values: IRegisterUserForm,
    formikHelpers: FormikHelpers<IRegisterUserForm>
  ) => {
    const { isActive, isTwoFactorEnabled, permissions } = values;
    const errors: DefaultError[] = [];

    if (
      values.permissions &&
      combinedInitialValues &&
      !isEqual(values.permissions, combinedInitialValues.permissions)
    ) {
      const updatedPermissions = middleware.user.formToQuery(values, userId);

      try {
        const checkResult = await updatePermissions.mutateAsync({
          permissions: updatedPermissions.permissions
        });

        if (checkResult && checkResult.requiredPermissions && checkResult.requiredPermissions.length > 0) {
          setShowCheckPermissionsPopup(true);
          setCurrentFormValues(values);
          setRequiredPermissionsData(checkResult.requiredPermissions);
          return;
        }

        await updatePermissionsMutation(
          { ...updatedPermissions },
          {
            onError: (error: DefaultError) => {
              errors.push(error);
            }
          }
        );
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    if (user?.isTwoFactorEnabled && !isTwoFactorEnabled) {
      try {
        await disableTwoFactor.mutateAsync(userId, {
          onError: (error: DefaultError) => {
            errors.push(error);
          }
        });
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    if (!user?.isTwoFactorEnabled && isTwoFactorEnabled) {
      try {
        await enabelTwoFactor.mutateAsync(userId, {
          onError: (error: DefaultError) => {
            errors.push(error);
          }
        });
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    if (user?.isActive && !isActive) {
      try {
        await deactivateUser.mutateAsync(undefined, {
          onError: (error: DefaultError) => {
            errors.push(error);
          }
        });
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    const { permissions: _, isActive: __, isTwoFactorEnabled: ___, ...userData } = values;
    const { permissions: initialPermissions, isActive: initialIsActive, isTwoFactorEnabled: initialIsTwoFactorEnabled, ...initialUserData } = combinedInitialValues;

    const userDataChanged = !isEqual(userData, initialUserData);

    if (userDataChanged) {
      try {
        await editUser.mutateAsync(
          {
            ...userData,
            isTwoFactorEnabled: values.isTwoFactorEnabled,
          },
          {
            onError: (error: DefaultError) => {
              errors.push(error);
            }
          }
        );
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    if ((!user?.isActive && isActive) || (user?.isActive && isActive && userDataChanged)) {
      try {
        await activateUser.mutateAsync(undefined, {
          onError: (error: DefaultError) => {
            errors.push(error);
          }
        });
      } catch (error) {
        errors.push(error as DefaultError);
      }
    }

    if (errors.length === 0) {
      navigate(`/settings/users/${userId}/permissions`);
    } else {
      errors.forEach(error => onErrorShowToast(error));
    }
  };

  return (
    <>
      <Form.User
        i18nextPrefix={i18nextPrefix}
        tittle={user?.userName ?? t("user.title.individual")}
        initialValues={combinedInitialValues}
        onSubmit={handleSubmit}
        backPath={backPathname}
        status={{ isLoading }}
        customBackItem={
          <Button
            onlyIcon
            onClick={() => {
              navigate(`${backPathname}`, { replace: true });
            }}
          >
            <Button.Description description={t('button.cancel')} />

            <Button.Icon icon={<PiX />} />
          </Button>
        }
        topbarContent={
          <div className="w-full flex justify-end items-center gap-8">
            <Button type="submit" variant={BUTTON_VARIANT.submit}>
              <Button.Text text={t("button.save")} />
              <Button.Icon icon={<PiVinylRecord />} />
            </Button>
          </div>
        }
      />
      
      <PopupCheckPermissions
        header={t(`permissions.popup.check-permissions-message-title`)}
        onCancel={handleCheckPermissionsCancel}
        onUpdate={handleCheckPermissionsUpdate}
        open={showCheckPermissionsPopup}
        onClose={() => {
          setShowCheckPermissionsPopup(false);
          setRequiredPermissionsData(undefined);
        }}
        trigger={<div style={{ display: 'none' }} />}
        formValues={currentFormValues}
        requiredPermissions={requiredPermissionsData}
      />
    </>
  );
};

export default EditUser;
function mutateOptions(
  helpers: any
):
  | import("@tanstack/react-query").MutateOptions<
      import("services/userManagement/types").IGetPermissionsResponse,
      Error,
      import("services/userManagement/types").IUserPermissionQuery,
      unknown
    >
  | undefined {
  throw new Error("Function not implemented.");
}
